import type {
  IPriceProductDraftPreview,
  IPriceProductQuickFillRuleChange,
  IPriceProductQuickFillRules,
  IPriceProductRow,
} from '../types';

import {
  PRICE_PRODUCT_FIELD,
  PRICE_PRODUCT_INPUT_PATTERN,
  PRICE_PRODUCT_QUICK_FILL_OPERATOR,
  PRICE_PRODUCT_QUICK_FILL_UNIT,
} from '../constants';
import { applyQuickFillRule, toControlAmount, toControlVariantBaseIDs } from '../utils';
import { useCallback, useMemo, useState } from 'react';

const EMPTY_RULE = {
  operator: PRICE_PRODUCT_QUICK_FILL_OPERATOR.INCREASE,
  value: '',
  unit: PRICE_PRODUCT_QUICK_FILL_UNIT.PERCENT,
} as const;

const EMPTY_RULES: IPriceProductQuickFillRules = {
  [PRICE_PRODUCT_FIELD.PRICE]: EMPTY_RULE,
  [PRICE_PRODUCT_FIELD.COMPARE_PRICE]: EMPTY_RULE,
};

export interface IUsePriceProductQuickFillParams {
  rows: IPriceProductRow[];
  /** Empty means the rule covers the whole table; otherwise it only touches what is ticked. */
  selectedBaseIDs: string[];
  onApply: (preview: IPriceProductDraftPreview) => void;
}

export interface IUsePriceProductQuickFillResult {
  isQuickFillOpen: boolean;
  quickFillRules: IPriceProductQuickFillRules;
  /** What the open rule would write, by variant baseID — what the fields show ahead of Apply. */
  quickFillPreview: IPriceProductDraftPreview;
  handleOpenQuickFill: () => void;
  handleChangeQuickFillRule: (change: IPriceProductQuickFillRuleChange) => void;
  handleApplyQuickFill: () => void;
  /** Empties and closes the rule bar without applying it. */
  handleResetQuickFill: () => void;
}

/**
 * The rule bar above the Variant columns. Nothing is written while it is open: the rule only yields a
 * preview the price fields render over their own amounts, so emptying it puts every field back to
 * what the merchant had typed, and Apply is the single point where the table actually changes.
 */
export const usePriceProductQuickFill = ({
  rows,
  selectedBaseIDs,
  onApply,
}: IUsePriceProductQuickFillParams): IUsePriceProductQuickFillResult => {
  const [isQuickFillOpen, setIsQuickFillOpen] = useState(false);
  const [quickFillRules, setQuickFillRules] = useState<IPriceProductQuickFillRules>(EMPTY_RULES);

  const quickFillPreview = useMemo<IPriceProductDraftPreview>(() => {
    if (!isQuickFillOpen) return {};

    const targetBaseIDs = new Set(
      selectedBaseIDs.length ? selectedBaseIDs : toControlVariantBaseIDs(rows),
    );
    const preview: IPriceProductDraftPreview = {};

    rows.forEach((row) =>
      row.variants.forEach((variant) => {
        if (!targetBaseIDs.has(variant.baseID)) return;

        const price = applyQuickFillRule({
          rule: quickFillRules[PRICE_PRODUCT_FIELD.PRICE],
          controlAmount: toControlAmount(variant.controlPrice),
        });
        const comparePrice = applyQuickFillRule({
          rule: quickFillRules[PRICE_PRODUCT_FIELD.COMPARE_PRICE],
          controlAmount: toControlAmount(variant.controlComparePrice),
        });
        if (price === undefined && comparePrice === undefined) return;

        preview[variant.baseID] = {
          ...(price !== undefined && { [PRICE_PRODUCT_FIELD.PRICE]: price }),
          ...(comparePrice !== undefined && { [PRICE_PRODUCT_FIELD.COMPARE_PRICE]: comparePrice }),
        };
      }),
    );

    return preview;
  }, [isQuickFillOpen, rows, selectedBaseIDs, quickFillRules]);

  const handleOpenQuickFill = useCallback(() => setIsQuickFillOpen(true), []);

  const handleChangeQuickFillRule = useCallback(
    ({ field, rule }: IPriceProductQuickFillRuleChange) => {
      if (rule.value !== undefined && !PRICE_PRODUCT_INPUT_PATTERN.test(rule.value)) return;

      setQuickFillRules((current) => {
        const next = { ...current[field], ...rule };

        // `=` names the amount outright, so a percentage of it would mean nothing — the unit goes back
        // to the shop's currency and the select that owns it is disabled alongside this.
        const isSetOperator = next.operator === PRICE_PRODUCT_QUICK_FILL_OPERATOR.SET;

        return {
          ...current,
          [field]: {
            ...next,
            unit: isSetOperator ? PRICE_PRODUCT_QUICK_FILL_UNIT.CURRENCY : next.unit,
          },
        };
      });
    },
    [],
  );

  const handleApplyQuickFill = useCallback(() => {
    onApply(quickFillPreview);
    setQuickFillRules(EMPTY_RULES);
    setIsQuickFillOpen(false);
  }, [onApply, quickFillPreview]);

  const handleResetQuickFill = useCallback(() => {
    setQuickFillRules(EMPTY_RULES);
    setIsQuickFillOpen(false);
  }, []);

  return {
    isQuickFillOpen,
    quickFillRules,
    quickFillPreview,
    handleOpenQuickFill,
    handleChangeQuickFillRule,
    handleApplyQuickFill,
    handleResetQuickFill,
  };
};
